import { useMemo } from 'react';
import { FILTER_DIMENSIONS } from '@/lib/filters';
import { findTagMatches } from '@/lib/tagSearch';

// Map FILTER_DIMENSIONS → { key: { label, emoji } } for the small label
// chip on each suggestion. Single source of truth lives in lib/filters.
const DIMENSION_META = FILTER_DIMENSIONS.reduce((acc, { key, label, emoji }) => {
  acc[key] = { label, emoji };
  return acc;
}, {});

// Typeahead dropdown for the hero search bar.
//
// Renders nothing when:
//   • `open` is false (parent closed it on blur / submit).
//   • The query is shorter than the visible threshold.
//   • Nothing matches.
// Positions itself as an absolute layer beneath the input — the caller
// is responsible for wrapping the input in a `relative` container so
// the dropdown sits correctly.
//
// Click handling: calls onToggleTag(dimensionKey, tagId). What that
// does is the parent's call — on LandingPage it navigates to /search
// with the new chip applied; on SearchResultsPage it writes the chip
// to the URL which auto-reruns the search. We don't mutate the typed
// query — chips layer on top of free-text, the input keeps its value.
// Already-selected matches are filtered out via `selected`, so a
// suggestion vanishes once added.
//
// Multi-pick UX: the dropdown stays open across picks (parent refocuses
// the input after each toggle), so the user can stack several chips
// from one typing session before dismissing.
export default function HeroSearchTypeahead({
  query,
  tagOptions,
  selected,
  onToggleTag,
  open = true,
  minQueryLength = 2,
  limit = 8,
}) {
  const trimmed = (query || '').trim();
  const active = open && trimmed.length >= minQueryLength;

  const matches = useMemo(() => {
    if (!active) return [];
    return findTagMatches(trimmed, tagOptions, {
      limit,
      selectedById: selected,
    });
  }, [active, trimmed, tagOptions, selected, limit]);

  if (!matches.length) return null;

  return (
    <div
      className="absolute left-0 right-0 top-full mt-2 z-30 bg-white rounded-2xl border border-[#E6E2D6] shadow-lg overflow-hidden"
      data-testid="hero-typeahead"
      role="listbox"
      aria-label="Tag suggestions"
    >
      <div className="px-4 py-2 text-xs uppercase tracking-wide text-[#6B665E] border-b border-[#E6E2D6]/70 bg-[#FBF4EB]">
        Suggested filters
      </div>
      <ul className="max-h-72 overflow-y-auto py-1">
        {matches.map(({ key, tag }) => {
          const meta = DIMENSION_META[key] || { label: key, emoji: '' };
          return (
            <li key={`${key}:${tag.id}`}>
              <button
                type="button"
                onClick={() => onToggleTag(key, tag.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#FBF4EB] focus:bg-[#FBF4EB] focus:outline-none transition-colors"
                data-testid={`hero-typeahead-${key}-${tag.id}`}
                role="option"
                aria-selected={false}
              >
                <span className="text-base shrink-0" aria-hidden>
                  {meta.emoji}
                </span>
                <span className="text-xs font-medium uppercase tracking-wide text-[#6B665E] shrink-0">
                  {meta.label}
                </span>
                <span className="text-sm text-[#2D2A26] truncate">
                  {tag.display_name}
                </span>
                {tag.sanskrit_name && (
                  <span className="text-xs italic text-[#6B665E] shrink-0">
                    ({tag.sanskrit_name})
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
